import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import FactoryRoundedIcon from '@mui/icons-material/FactoryRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingFlatRoundedIcon from '@mui/icons-material/TrendingFlatRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import EnergyApiService from '../../api/EnergyApiService';
import { formatQuantity } from '../Energy/energyEntryUtils';

const energyApiService = new EnergyApiService();
const axisNumberFormatter = new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
});

function formatAxisValue (value) {
    return axisNumberFormatter.format(value).toLowerCase();
}

function percentChange (current, prior) {
    if (!prior) return current ? null : 0;
    return ((current - prior) / prior) * 100;
}

export default function DashboardEnergyPerformanceSummary ({ jsonSettings }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [summary, setSummary] = useState(null);
    const commodity = useMemo(() => {
        try {
            const settings = typeof jsonSettings === 'string' ? JSON.parse(jsonSettings) : jsonSettings;
            return settings?.commodity === 'naturalGas' ? 'naturalGas' : 'electricity';
        }
        catch {
            return 'electricity';
        }
    }, [jsonSettings]);

    useEffect(() => {
        let ignore = false;

        async function load () {
            setLoading(true);
            setError('');

            try {
                const response = await energyApiService.getDashboardPerformanceSummary();
                if (!ignore) {
                    if (response?.success) {
                        setSummary(response.data);
                    } else {
                        setError(response?.message ?? t('dashboardEnergy.error.load'));
                    }
                }
            }
            catch (err) {
                console.error('Error loading energy performance summary:', err);
                if (!ignore) {
                    setError(err.message ?? t('dashboardEnergy.error.load'));
                }
            }
            finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        }

        load();

        return () => {
            ignore = true;
        };
    }, []);

    const trendData = useMemo(() => {
        if (!summary) return [];

        return summary[commodity].monthly
            .filter(point => point.currentHasData)
            .map(point => ({ month: point.month, value: point.current }));
    }, [summary, commodity]);

    const topMeter = useMemo(() => {
        if (!summary) return null;

        const candidates = [
            ...summary.electricity.meters.map(item => ({
                ...item,
                type: t('dashboardEnergy.metric.electricity'),
                share: summary.electricity.current ? item.value / summary.electricity.current : 0,
            })),
            ...summary.naturalGas.meters.map(item => ({
                ...item,
                type: t('dashboardEnergy.metric.naturalGas'),
                share: summary.naturalGas.current ? item.value / summary.naturalGas.current : 0,
            })),
        ].sort((a, b) => b.share - a.share);

        return candidates[0] ?? null;
    }, [summary, t]);

    if (loading) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%' }}>
                <CircularProgress size={30} sx={{ color: '#2563eb' }} />
            </Stack>
        );
    }

    if (error) {
        return (
            <Stack justifyContent="center" sx={{ width: '100%', height: '100%', p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Stack>
        );
    }

    if (!summary || !summary.hasVerifiedData) {
        return (
            <Stack justifyContent="center" sx={{ width: '100%', height: '100%', p: 2 }}>
                <Alert severity="info">{t('dashboardEnergy.empty.noData')}</Alert>
            </Stack>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
                p: 1.5,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                background: 'transparent',
            }}
        >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 850, lineHeight: 1.1, color: '#111827' }}>
                        {t('dashboardEnergy.title')}
                    </Typography>
                    <Typography sx={{ mt: 0.25, fontSize: 12, color: '#64748b', fontWeight: 650 }}>
                        {t('dashboardEnergy.subtitle', {
                            year: summary.currentYear,
                            priorYear: summary.priorYear,
                            month: summary.verifiedMonth,
                        })}
                    </Typography>
                </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8} sx={{ flexShrink: 0 }}>
                <EnergyMetric
                    icon={<BoltRoundedIcon fontSize="small" />}
                    label={t('dashboardEnergy.metric.electricity')}
                    value={summary.electricity.current}
                    prior={summary.electricity.prior}
                    unit={summary.electricity.unit || 'kWh'}
                    color="#2563eb"
                />
                <EnergyMetric
                    icon={<LocalFireDepartmentRoundedIcon fontSize="small" />}
                    label={t('dashboardEnergy.metric.naturalGas')}
                    value={summary.naturalGas.current}
                    prior={summary.naturalGas.prior}
                    unit={summary.naturalGas.unit || 'therms'}
                    color="#dc6b19"
                />
                <ProductionMetric production={summary.production} electricity={summary.electricity} />
            </Stack>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 120,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(190px, 0.85fr) minmax(220px, 1.15fr)' },
                    gap: 1.25,
                    alignItems: 'stretch',
                }}
            >
                <Stack spacing={0.8} sx={{ minWidth: 0 }}>
                    <ContributorCard
                        title={t('dashboardEnergy.top.location')}
                        label={summary.sites[0]?.label ?? t('common.none')}
                        value={summary.sites[0]?.value ?? 0}
                        total={summary.sites.reduce((total, site) => total + site.value, 0)}
                    />
                    <ContributorCard
                        title={t('dashboardEnergy.top.meter')}
                        label={topMeter ? `${topMeter.type}: ${topMeter.label}` : t('common.none')}
                        value={topMeter?.share ?? 0}
                        total={1}
                    />
                </Stack>

                <Box sx={{ minWidth: 0, minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trendData} margin={{ top: 10, right: 10, bottom: 0, left: -12 }}>
                            <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={1} />
                            <YAxis tick={{ fontSize: 10 }} width={44} tickFormatter={formatAxisValue} />
                            <Tooltip formatter={(value) => value === null ? '—' : formatQuantity(value, 0)} />
                            <Line type="monotone" dataKey="value" stroke={commodity === 'naturalGas' ? '#dc6b19' : '#2563eb'} strokeWidth={2.5} dot={false} name={t(`dashboardEnergy.metric.${commodity}`)} />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </Box>
        </Box>
    );
}

function EnergyMetric ({ icon, label, value, prior, unit, color }) {
    const { t } = useTranslation();
    const change = percentChange(value, prior);
    const TrendIcon = change === null || Math.abs(change) < 1
        ? TrendingFlatRoundedIcon
        : change > 0
            ? TrendingUpRoundedIcon
            : TrendingDownRoundedIcon;

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                border: '1px solid #e5e7eb',
                borderRadius: 1,
                backgroundColor: '#ffffff',
                px: 1,
                py: 0.85,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box sx={{ color, display: 'flex' }}>{icon}</Box>
                <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 850, textTransform: 'uppercase' }} noWrap>
                    {label}
                </Typography>
            </Stack>
            <Typography sx={{ mt: 0.4, fontSize: 22, lineHeight: 1, color: '#111827', fontWeight: 900 }} noWrap>
                {formatQuantity(value, 0)}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ mt: 0.25 }}>
                <TrendIcon sx={{ fontSize: 15, color: change > 0 ? '#b45309' : '#15803d' }} />
                <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 750 }} noWrap>
                    {change === null ? t('dashboardEnergy.change.noPrior') : t('dashboardEnergy.change.percent', { value: Math.abs(change).toFixed(1), unit })}
                </Typography>
            </Stack>
        </Box>
    );
}

function ProductionMetric ({ production, electricity }) {
    const { t } = useTranslation();
    const intensity = production.currentSold ? electricity.current / production.currentSold : null;

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                border: '1px solid #e5e7eb',
                borderRadius: 1,
                backgroundColor: '#ffffff',
                px: 1,
                py: 0.85,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={0.75}>
                <FactoryRoundedIcon sx={{ fontSize: 18, color: '#475569' }} />
                <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 850, textTransform: 'uppercase' }} noWrap>
                    {t('dashboardEnergy.metric.intensity')}
                </Typography>
            </Stack>
            <Typography sx={{ mt: 0.4, fontSize: 22, lineHeight: 1, color: '#111827', fontWeight: 900 }} noWrap>
                {intensity === null ? '—' : formatQuantity(intensity, 1)}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: 11, color: '#64748b', fontWeight: 750 }} noWrap>
                {production.currentSold
                    ? t('dashboardEnergy.metric.perSoldUnit', { unit: production.unit || t('dashboardEnergy.metric.unit') })
                    : t('dashboardEnergy.metric.noProduction')}
            </Typography>
        </Box>
    );
}

function ContributorCard ({ title, label, value, total }) {
    const percent = total ? Math.min(100, Math.round((value / total) * 100)) : 0;

    return (
        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 0.9, backgroundColor: '#ffffff' }}>
            <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 850, textTransform: 'uppercase' }} noWrap>
                {title}
            </Typography>
            <Typography sx={{ mt: 0.2, fontSize: 13, color: '#111827', fontWeight: 850 }} noWrap>
                {label}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                <LinearProgress
                    variant="determinate"
                    value={percent}
                    sx={{
                        flex: 1,
                        height: 7,
                        borderRadius: 1,
                        backgroundColor: '#e5e7eb',
                        '& .MuiLinearProgress-bar': { backgroundColor: '#99C20E' },
                    }}
                />
                <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 800, minWidth: 34, textAlign: 'right' }}>
                    {percent}%
                </Typography>
            </Stack>
        </Box>
    );
}
